// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — pierTex.js   ·   THE SHARED PAINT BOX
// ────────────────────────────────────────────────────────────────────
// The seeded PRNG, the texture cache, the painted-word helper, the font
// generation, and the geometry merger — everything `Pier3D.jsx` and
// `pierBuild.jsx` both need.
//
// ⚑ IT EXISTS SO THE IMPORT GRAPH HAS NO CYCLE. The build kit cannot
// import Pier3D (Pier3D imports the kit), so the helpers moved DOWN into
// a module both import. A kit never imports a screen.
// ════════════════════════════════════════════════════════════════════

import { useEffect, useState } from "react";
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

// ── SEEDED PRNG — never Math.random(), and never during render ──────
export function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// A stable small integer from a string id, for seeding per-building variety.
export function hashId(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

// ── THE TEXTURE CACHE — every procedural texture, by key ────────────
export const TEX = new Map();
export function canvasTex(key, size, paint, h = size) {
  if (TEX.has(key)) return TEX.get(key);
  const cv = document.createElement("canvas");
  cv.width = size; cv.height = h;
  paint(cv.getContext("2d"), size, h);
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  TEX.set(key, t);
  return t;
}

// ── ⭐⭐ THE FONT GENERATION ──────────────────────────────────────────
// A canvas `fillText` bakes whatever face is loaded AT PAINT TIME, and
// these textures are cached forever — so the generation is part of every
// text key, and signs repaint once Cinzel lands.
export let FONT_GEN = 0;
const _fontWatchers = new Set();
// ⚠ WAIT FOR CINZEL ITSELF, NOT `document.fonts.ready`. `ready` resolves as
// soon as nothing is PENDING — and at module load nothing has asked for
// Cinzel yet, so it resolved at boot, FONT_GEN became 1 before a single sign
// was painted, and on a fast `?uid=` handoff every sign could bake the
// fallback serif for the whole session. `load()` actually requests the face
// and settles when it has arrived (or failed — then the fallback is final).
// (Third adversarial review.)
if (typeof document !== "undefined" && document.fonts?.load) {
  const bump = () => {
    FONT_GEN = 1;
    _fontWatchers.forEach((fn) => { try { fn(); } catch {} });
  };
  Promise.all([
    document.fonts.load("900 64px Cinzel"),
    document.fonts.load("700 64px Cinzel"),
  ]).then(bump, bump);
}

// ⭐ LET GO OF THE GPU SIDE OF EVERY CACHED TEXTURE — call on renderer unmount.
// Each renderer that uploads a cached texture hangs its own 'dispose' listener
// on it, and R3F's Canvas unmount never disposes the renderer, so every trip
// into the Hall or the Minerium left the old renderer reachable through
// these listeners, forever. dispose() fires that event and frees the GL side;
// the canvas stays in the cache and simply uploads again on next use.
// (Third adversarial review, CONFIRMED against three 0.185 WebGLTextures.)
export function releaseTextures() {
  TEX.forEach((t) => { try { t.dispose(); } catch {} });
}
export function watchFonts(fn) {
  if (FONT_GEN) return () => {};
  _fontWatchers.add(fn);
  return () => _fontWatchers.delete(fn);
}

// ⭐ The font generation as a hook: re-renders once when the real face lands.
// ⚠ It compares what RENDER saw with the live value inside the effect, so a
// face that lands between the render and the effect still repaints — the
// watcher alone would have been handed a no-op and the signs kept the
// fallback serif for the whole session. (Second adversarial review.)
export function useFontGen() {
  const [, force] = useState(0);
  const seen = FONT_GEN;
  useEffect(() => {
    if (FONT_GEN !== seen) { force((v) => v + 1); return undefined; }
    return watchFonts(() => force((v) => v + 1));
  }, [seen]);
  return FONT_GEN;
}

// A painted word, glowing. Cached by text+colour+font.
export function textTex(text, color, opts = {}) {
  const { font = "700 64px Cinzel, Palatino Linotype, serif", glow = color, track = 6 } = opts;
  const key = `txt|${FONT_GEN}|${text}|${color}|${font}|${track}|${glow}`;
  if (TEX.has(key)) return TEX.get(key);
  const cv = document.createElement("canvas");
  const ctx0 = cv.getContext("2d");
  ctx0.font = font;
  const w = Math.max(64, Math.ceil(ctx0.measureText(text).width + track * text.length + 48));
  cv.width = THREE.MathUtils.ceilPowerOfTwo(w);
  cv.height = 128;
  const ctx = cv.getContext("2d");
  ctx.font = font;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.shadowColor = glow;
  ctx.shadowBlur = 26;
  ctx.fillStyle = color;
  ctx.fillText(text, cv.width / 2, cv.height / 2);
  ctx.shadowBlur = 0;
  ctx.fillText(text, cv.width / 2, cv.height / 2);
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  t.userData.aspect = cv.width / cv.height;
  TEX.set(key, t);
  return t;
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE MERGER — a building is a handful of draw calls, not a hundred
// ────────────────────────────────────────────────────────────────────
// Every box, post and plank of a structure is transformed into place and
// merged BY MATERIAL, so a shopfront with sixty parts is ~eight draws.
// ⚠ mergeGeometries requires every input to agree on indexed-ness and on
// attribute names. Box, cylinder, plane, sphere and cone are all indexed
// with position/normal/uv, so the kit uses only those.
// ════════════════════════════════════════════════════════════════════
const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _p = new THREE.Vector3();
const _s = new THREE.Vector3();

// ⭐ BOX-MAPPED UVs. A primitive's own UVs run 0→1 per face, so a 6-unit
// wall and a 0.3-unit post would stretch the same clapboard over both.
// Projecting by normal gives every surface the SAME texel size in world
// units — boards stay boards and shingles stay shingles at any size.
export function boxUV(geo, scale = 1) {
  const p = geo.attributes.position, n = geo.attributes.normal, uv = geo.attributes.uv;
  if (!p || !n || !uv) return geo;
  for (let i = 0; i < p.count; i++) {
    const ax = Math.abs(n.getX(i)), ay = Math.abs(n.getY(i)), az = Math.abs(n.getZ(i));
    let u, v;
    if (ay >= ax && ay >= az) { u = p.getX(i); v = p.getZ(i); }
    else if (ax >= az)        { u = p.getZ(i); v = p.getY(i); }
    else                      { u = p.getX(i); v = p.getY(i); }
    uv.setXY(i, u * scale, v * scale);
  }
  uv.needsUpdate = true;
  return geo;
}

export class Merger {
  constructor() { this.parts = {}; }
  // pos [x,y,z] · rot [x,y,z] radians · scl [x,y,z]
  add(mat, geo, pos = [0, 0, 0], rot = [0, 0, 0], scl = [1, 1, 1]) {
    _e.set(rot[0], rot[1], rot[2]);
    _q.setFromEuler(_e);
    _p.set(pos[0], pos[1], pos[2]);
    _s.set(scl[0], scl[1], scl[2]);
    _m.compose(_p, _q, _s);
    const g = geo.index ? geo : geo;   // all kit primitives are indexed
    g.applyMatrix4(_m);
    (this.parts[mat] || (this.parts[mat] = [])).push(g);
    return this;
  }
  box(mat, w, h, d, pos, rot) { return this.add(mat, new THREE.BoxGeometry(w, h, d), pos, rot); }
  cyl(mat, rTop, rBot, h, seg, pos, rot, open = false) {
    return this.add(mat, new THREE.CylinderGeometry(rTop, rBot, h, seg, 1, open), pos, rot);
  }
  plane(mat, w, h, pos, rot) { return this.add(mat, new THREE.PlaneGeometry(w, h), pos, rot); }
  // Apply a group transform to everything added so far (for mirroring a
  // whole building round to face the other way).
  transformAll(pos, rotY) {
    _e.set(0, rotY, 0); _q.setFromEuler(_e);
    _p.set(pos[0], pos[1], pos[2]); _s.set(1, 1, 1);
    _m.compose(_p, _q, _s);
    for (const k in this.parts) for (const g of this.parts[k]) g.applyMatrix4(_m);
    return this;
  }
  // → { [materialKey]: BufferGeometry }
  build() {
    const out = {};
    for (const k in this.parts) {
      const geos = this.parts[k];
      out[k] = geos.length === 1 ? geos[0] : mergeGeometries(geos, false);
      if (geos.length > 1) geos.forEach((g) => g.dispose());
    }
    this.parts = {};
    return out;
  }
}
